import FingerprintJS from "@fingerprintjs/fingerprintjs";
import PropTypes from "prop-types";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import toastr from "toastr";
import { buildReplacementUpdateToast } from "../helpers/replacementUpdateSocket";
import { useSelector } from "react-redux";
import { store } from "../store/index";
import {
  selectAccessToken,
  selectIsAuthenticated,
  selectUser,
} from "../store/auth/selectors";
import config from "../config/index";

// Socket server URL - matches the tunnel URL used in api-client.js
// You can set this via environment variable: import.meta.env.VITE_SOCKET_URL
const getSocketURL = () => {
  if (config.socketURL?.includes("api/v1")) {
    return config.socketURL.replace("/api/v1", "");
  }
  return config.socketURL;
};

let socketInstance = null;
const fpPromise = FingerprintJS.load();
const SocketContext = createContext(null);

/**
 * FingerprintJS visitor id (no persistence)
 */
const getDeviceId = async () => {
  const fp = await fpPromise;
  const result = await fp.get();
  return result.visitorId;
};

/**
 * Get user ID from stored auth user
 */
const getUserId = () => selectUser(store.getState())?.id;

const emitJoinEvent = async (socket) => {
  const userId = getUserId();
  let deviceId;

  try {
    deviceId = await getDeviceId();
  } catch (error) {
    console.error("[useSocket] Failed to get FingerprintJS visitorId:", error);
    return;
  }

  socket.emit("join", {
    id: userId,
    deviceId,
  });
};

export const SocketProvider = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const accessToken = useSelector(selectAccessToken);
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    // Only connect if user is authenticated
    if (isAuthenticated && accessToken) {
      // Create new connection if one doesn't exist
      if (!socketInstance) {
        const socketURL = getSocketURL();

        // Initialize socket connection
        socketInstance = io(socketURL, {
          auth: {
            token: accessToken,
          },
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5,
        });

        socketInstance.on("connect", async () => {
          console.log("[useSocket] Socket connected:", socketInstance.id);
          setIsConnected(true);
          await emitJoinEvent(socketInstance);
        });

        socketInstance.on("disconnect", () => {
          setIsConnected(false);
        });

        socketInstance.on("connect_error", (error) => {
          console.error("[useSocket] Socket connection error:", error);
          setIsConnected(false);
        });

        socketInstance.on("reconnect", async (attemptNumber) => {
          console.log(
            "[useSocket] Socket reconnected after",
            attemptNumber,
            "attempts"
          );
          setIsConnected(true);
          await emitJoinEvent(socketInstance);
        });

        socketInstance.on("replacement-update", (data) => {
          const { message, toastType } = buildReplacementUpdateToast(
            data ?? {}
          );
          if (toastType === "success") {
            toastr.success(message);
          } else {
            toastr.info(message);
          }
        });

        setSocket(socketInstance);
      } else {
        // If socket already exists, just update the state
        setSocket(socketInstance);
        setIsConnected(socketInstance.connected);
      }
    } else {
      // Disconnect when user logs out
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance.off("replacement-update");
        socketInstance = null;
        setIsConnected(false);
        setSocket(null);
      }
    }

    // Cleanup function
    return () => {
      // Note: We don't disconnect here if user is still authenticated
      // Socket will persist across component re-renders
      // Only disconnect when authentication state changes to false
    };
  }, [isAuthenticated, accessToken]);

  const value = useMemo(
    () => ({
      socket,
      isConnected,
    }),
    [socket, isConnected]
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
};

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Helper to update socket server URL
 * Call this function when you have the endpoint
 */
export const setSocketURL = (url) => {
  // This will be used when user provides the endpoint
  console.log("Socket URL will be set to:", url);
};
